import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from './axiosConfig';
import { 
  Paper, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Tabs,
  Tab,
  Box,
  Divider,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import HomeIcon from '@mui/icons-material/Home';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import DownloadIcon from '@mui/icons-material/Download';

const PayrollCalculator = () => {
  const navigate = useNavigate();

  // NOTE: rates starts as null, not a hardcoded 2024/25 fallback object.
  // Rates now come exclusively from the backend (payroll_rates table).
  // If the backend can't find a rate, it omits that key rather than
  // guessing — so this component must handle rates being null/incomplete
  // rather than always having a plausible-looking number to show.
  const [payrollData, setPayrollData] = useState({
    employees: [],
    rates: null,
    year: new Date().getFullYear()
  });
  
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [weeksToForecast, setWeeksToForecast] = useState(52);
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Next 1 April that is still in the future. Before 1 April this year it
  // returns this year's April; on or after it, next year's. (The old code
  // always used next calendar year, which skipped a whole April change
  // when used between 1 January and 31 March.)
  const getFutureAprilDate = (fromDate = new Date()) => {
    const y = fromDate.getFullYear();
    return new Date(fromDate < new Date(y, 3, 1) ? y : y + 1, 3, 1);
  };
  
  // Fetch data from backend
  const fetchData = async () => {
    setLoading(true);
    try {
      console.log("Fetching payroll calculator data");
      
      // Add timestamp for cache busting
      const timestamp = new Date().getTime();
      
      // Make API request with cache-busting parameter
      const response = await api.get(`/payroll/calculator-data?t=${timestamp}`);
      
      console.log("Payroll API response:", response.data);
      
      // Check if we need to enhance the API response with additional calculations
      if (response.data && response.data.employees) {
        const employeesWithCalculations = calculateEmployeeData(
          response.data.employees, 
          response.data.rates
        );
        
        // Update state with response data and calculated values
        setPayrollData({
          ...response.data,
          employees: employeesWithCalculations
        });
      } else {
        // If no employees data, just update the state with response data
        setPayrollData(response.data);
      }
    } catch (error) {
      console.error('Error fetching payroll data:', error);
      setError('Failed to fetch payroll data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Get age of employee on a specific date
  const getAgeOnDate = (dateOfBirth, targetDate) => {
    if (!dateOfBirth) return null;
    
    // Parse dates
    const dob = new Date(dateOfBirth);
    const date = new Date(targetDate);
    
    // Calculate age
    let age = date.getFullYear() - dob.getFullYear();
    
    // Adjust age if birthday hasn't occurred yet in the target year
    const hasBirthdayOccurred = (
      date.getMonth() > dob.getMonth() || 
      (date.getMonth() === dob.getMonth() && date.getDate() >= dob.getDate())
    );
    
    if (!hasBirthdayOccurred) {
      age--;
    }
    
    return age;
  };

  // Determine NMW rate category based on age and apprentice status
  const getRateCategory = (age, isApprentice) => {
    if (isApprentice) return 'apprentice';
    if (age >= 21) return 'over21';
    if (age >= 18) return 'age18to20';
    return 'under18';
  };

  // Determine if an employee is on NMW or has a custom rate.
  // Guards against individual NMW rates being missing (null) from the
  // backend, which is now a legitimate possibility rather than always
  // having a hardcoded number to fall back to.
  const isOnNMW = (hourlyRate, rates) => {
    const nmwRates = [
      rates.current.over21,
      rates.current.age18to20,
      rates.current.under18,
      rates.current.apprentice
    ].filter(rate => rate != null);

    if (nmwRates.length === 0) return false;

    // Check if hourly rate matches any NMW rate (with small margin for floating point comparison)
    return nmwRates.some(rate => Math.abs(hourlyRate - rate) < 0.01);
  };
  
  // Calculate all employee data including current and future costs
  const calculateEmployeeData = (employees, rates) => {
    // If we have no rates at all (e.g. backend returned nothing usable),
    // don't compute anything — flag every employee as having unavailable
    // rates so the UI can show that clearly instead of rendering £0.00 or
    // NaN figures that look like real numbers.
    if (!rates || !rates.current) {
      return employees.map(emp => ({
        ...emp,
        ratesIncomplete: true,
        futureRatesIncomplete: true
      }));
    }

    const currentDate = new Date();
    const futureDate = getFutureAprilDate(currentDate); // next 1 April still to come
    
    return employees.map(emp => {
      // Handle null hourly rate
      const hourlyRate = emp.hourlyRate || 0;
      
      // Store contracted hours
      const contractedHours = emp.contractedHours || emp.hours;
      const hoursToUse = contractedHours;
      
      // Current payroll calculation
      // Salaried staff: weekly pay is simply salary / 52. The hourly rate
      // sent by the backend is derived (salary / 52 / contracted hours)
      // and is used only for minimum wage checks and April forecasting.
      const isSalaried = emp.payType === 'salaried' && Number(emp.annualSalary) > 0;
      const weeklySalary = isSalaried ? Number(emp.annualSalary) / 52 : null;
      const salaryNoHours = isSalaried && !(hoursToUse > 0);
      const currentPayroll = isSalaried ? weeklySalary : hourlyRate * hoursToUse;
      
      // Determine if employee is on NMW or custom rate
      const onNMW = isOnNMW(hourlyRate, rates);
      
      // Get current and future ages
      const currentAge = emp.dateOfBirth ? getAgeOnDate(emp.dateOfBirth, currentDate) : null;
      const futureAge = emp.dateOfBirth ? getAgeOnDate(emp.dateOfBirth, futureDate) : null;
      
      // Is this employee's actual rate below the legal minimum for their age/status?
      const correctCategory = currentAge ? getRateCategory(currentAge, emp.isApprentice) : null;
      const requiredRate = correctCategory ? rates.current[correctCategory] : null;
      const belowNMW = !salaryNoHours && requiredRate != null && hourlyRate < requiredRate - 0.01;
      
      // Determine future rate. rates.increased may be entirely absent, or
      // individual keys within it may be null — both are now legitimate
      // "we don't know yet" states rather than bugs.
      let futureRate = hourlyRate;
      const futureRatesAvailable = !!rates.increased;

      if (futureRatesAvailable && !salaryNoHours && (onNMW || currentAge)) {
        const currentCategory = currentAge ? 
          getRateCategory(currentAge, emp.isApprentice) : 
          Object.entries(rates.current).find(([_, rate]) => rate != null && Math.abs(rate - hourlyRate) < 0.01)?.[0];
          
        const futureCategory = futureAge ? 
          getRateCategory(futureAge, emp.isApprentice) : 
          currentCategory;
        
        if (futureCategory && rates.increased[futureCategory] != null) {
          // Never lower someone's pay: the statutory minimum is a floor,
          // not a replacement for a rate that is already higher.
          futureRate = Math.max(hourlyRate, rates.increased[futureCategory]);
        }
      }
      
      // Future payroll calculation
      // Salaried staff stay on their salary unless it would fall below the
      // new minimum, in which case they are costed at the minimum.
      const futurePayroll = isSalaried
        ? ((salaryNoHours || futureRate <= hourlyRate + 0.0001) ? weeklySalary : futureRate * hoursToUse)
        : futureRate * hoursToUse;
      
      // Calculate ENIC - using threshold. Both enic % and enic_threshold
      // must be present; if either is missing we can't safely compute
      // this line item, so it's reported as 0 with a flag rather than
      // a number derived from a missing rate.
      const enicRateAvailable = rates.current.enic != null && rates.current.enic_threshold != null;
      const weeklyThreshold = enicRateAvailable ? rates.current.enic_threshold / 52 : null;
      let enicPayable = 0;

      if (enicRateAvailable && currentPayroll > weeklyThreshold) {
        enicPayable = (currentPayroll - weeklyThreshold) * (rates.current.enic / 100);
      }
      
      // Calculate employer pension contribution from the live rate
      // (was hardcoded to 0.03 — now sourced from payroll_rates so it
      // stays correct if the statutory minimum ever changes).
      const pensionRateAvailable = rates.current.pension_rate != null;
      const employerPension = pensionRateAvailable ? currentPayroll * rates.current.pension_rate : 0;
      
      // Holiday pay is NOT added on top. Staff on regular hours are paid
      // their normal wage through holiday, so the 5.6 weeks is already
      // inside the 52 weeks of wages. Adding the 12.07% accrual rate here
      // counted it twice. (The 12.07% is still right for costing an hour
      // actually worked, e.g. service pricing, or for ad hoc extra hours.)
      const holidayPay = 0;
      
      // Calculate total cost and cost per hour
      const totalWeeklyCost = currentPayroll + enicPayable + employerPension + holidayPay;
      const costPerHour = hoursToUse > 0 ? totalWeeklyCost / hoursToUse : 0;
      
      // Future cost calculations — same missing-rate guards as above
      const futureEnicRateAvailable = futureRatesAvailable && rates.increased.enic != null && rates.increased.enic_threshold != null;
      const futureWeeklyThreshold = futureEnicRateAvailable ? rates.increased.enic_threshold / 52 : null;
      let futureEnicPayable = 0;

      if (futureEnicRateAvailable && futurePayroll > futureWeeklyThreshold) {
        futureEnicPayable = (futurePayroll - futureWeeklyThreshold) * (rates.increased.enic / 100);
      }
      
      const futurePensionRateAvailable = futureRatesAvailable && rates.increased.pension_rate != null;
      const futureEmployerPension = futurePensionRateAvailable ? futurePayroll * rates.increased.pension_rate : 0;

      // Holiday pay is already inside wages (see the note above), so no extra line.
      const futureHolidayPay = 0;

      const futureTotalWeeklyCost = futurePayroll + futureEnicPayable + futureEmployerPension + futureHolidayPay;
      const futureCostPerHour = hoursToUse > 0 ? futureTotalWeeklyCost / hoursToUse : 0;
      
      return {
        ...emp,
        contractedHours,
        hours: hoursToUse,
        currentAge,
        futureAge,
        onNMW,
        belowNMW, 
        requiredRate,
        // Surfaced so the table/breakdown views can flag a row whose
        // total cost is built on incomplete rate data, rather than
        // presenting it identically to a fully-known figure.
        ratesIncomplete: !enicRateAvailable || !pensionRateAvailable,
        futureRatesIncomplete: !futureRatesAvailable || !futureEnicRateAvailable || !futurePensionRateAvailable,
        currentPayroll: currentPayroll.toFixed(2),
        futureRate: futureRate,
        isSalaried,
        salaryNoHours,
        futurePayroll: futurePayroll.toFixed(2),
        enicPayable: enicPayable.toFixed(2),
        employerPension: employerPension.toFixed(2),
        holidayPay: holidayPay.toFixed(2),
        totalWeeklyCost: totalWeeklyCost.toFixed(2),
        costPerHour: costPerHour.toFixed(2),
        futureEnicPayable: futureEnicPayable.toFixed(2),
        futureEmployerPension: futureEmployerPension.toFixed(2),
        futureHolidayPay: futureHolidayPay.toFixed(2),
        futureTotalWeeklyCost: futureTotalWeeklyCost.toFixed(2),
        futureCostPerHour: futureCostPerHour.toFixed(2)
      };
    });
  };
  
  // Initial fetch on mount
  useEffect(() => {
    console.log("PayrollCalculator component mounted");
    fetchData();
    
    // Cleanup on unmount — reset to null rates, not a refabricated
    // 2024/25 default set.
    return () => {
      console.log("PayrollCalculator component unmounting");
      setPayrollData({
        employees: [],
        rates: null,
        year: new Date().getFullYear()
      });
    };
  }, []);

  // Calculate the totals for current rates
  const calculateCurrentTotals = () => {
    return payrollData.employees.reduce((acc, emp) => {
      return {
        totalEmployees: acc.totalEmployees + Number(emp.count || 1),
        totalPayroll: acc.totalPayroll + Number(emp.currentPayroll || 0),
        totalEnic: acc.totalEnic + Number(emp.enicPayable || 0),
        totalPension: acc.totalPension + Number(emp.employerPension || 0),
        totalHoliday: acc.totalHoliday + Number(emp.holidayPay || 0),
        totalCost: acc.totalCost + Number(emp.totalWeeklyCost || 0)
      };
    }, { 
      totalEmployees: 0, 
      totalPayroll: 0,
      totalEnic: 0,
      totalPension: 0,
      totalHoliday: 0,
      totalCost: 0
    });
  };

  // Calculate the totals for future rates
  const calculateFutureTotals = () => {
    return payrollData.employees.reduce((acc, emp) => {
      return {
        totalEmployees: acc.totalEmployees + Number(emp.count || 1),
        totalPayroll: acc.totalPayroll + Number(emp.futurePayroll || 0),
        totalEnic: acc.totalEnic + Number(emp.futureEnicPayable || 0),
        totalPension: acc.totalPension + Number(emp.futureEmployerPension || 0),
        totalHoliday: acc.totalHoliday + Number(emp.futureHolidayPay || 0),
        totalCost: acc.totalCost + Number(emp.futureTotalWeeklyCost || 0)
      };
    }, { 
      totalEmployees: 0, 
      totalPayroll: 0,
      totalEnic: 0,
      totalPension: 0,
      totalHoliday: 0,
      totalCost: 0
    });
  };

  // Calculate the totals once and store them
  const currentTotals = calculateCurrentTotals();
  const futureTotals = calculateFutureTotals();

  // Calculate annual difference between current and future costs
  // Rates can be identical when next April's NMW rates haven't been announced yet
  // (the future rates simply mirror current ones). In that case the diff should be
  // exactly £0, not a few pence of floating-point noise from chained toFixed() conversions.
  const calculateAnnualDifference = () => {
    const weeklyDiff = futureTotals.totalCost - currentTotals.totalCost;
    if (Math.abs(weeklyDiff) < 0.01) return 0;
    return weeklyDiff * weeksToForecast;
  };

  // Calculate effective NIC percentage
  const calculateEffectiveNIC = () => {
    if (currentTotals.totalPayroll === 0) return 0;
    return (currentTotals.totalEnic / currentTotals.totalPayroll) * 100;
  };

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2
    }).format(value || 0);
  };
  
  if (loading) {
    return (
      <Paper elevation={3} sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
        <CircularProgress />
      </Paper>
    );
  }
  
  if (error) {
    return (
      <Paper elevation={3} sx={{ p: 3 }}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
        <Button 
          variant="contained" 
          startIcon={<RefreshIcon />} 
          onClick={fetchData}
          sx={{ mt: 2 }}
        >
          Retry
        </Button>
      </Paper>
    );
  }

  // If the backend couldn't resolve current rates at all (e.g. the
  // payroll_rates table is unseeded, or every lookup hit "no_type"),
  // show a clear message rather than rendering £0.00 / NaN across the
  // whole page. This is the explicit, visible version of what used to
  // be silently masked by hardcoded defaults.
  if (!payrollData.rates || !payrollData.rates.current) {
    return (
      <Paper elevation={3} sx={{ p: 3 }}>
        <Alert severity="warning">
          Payroll rates are not currently available. This usually means the
          rate tables haven't been set up yet for this period. Contact
          support if this persists.
        </Alert>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={fetchData}
          sx={{ mt: 2 }}
        >
          Retry
        </Button>
      </Paper>
    );
  }

  // Employment allowance — no hardcoded £5,000 fallback. If the backend
  // didn't resolve this rate, employmentAllowance is null and every
  // calculation below that depends on it degrades visibly rather than
  // silently using a stale figure.
  const employmentAllowance = payrollData.rates.current.employment_allowance;
  const annualNIC = currentTotals.totalEnic * weeksToForecast;
  const nicAfterAllowance = employmentAllowance != null
    ? Math.max(0, annualNIC - employmentAllowance)
    : null;

  // Year of the next April change, used in the tab and rate card labels
  const futureAprilYear = getFutureAprilDate().getFullYear();
  
  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 2 }}>
      {/* Header */}
      <Paper 
        elevation={0} 
        sx={{ 
          background: 'linear-gradient(135deg, #059669, #0d9488)', 
          color: 'white', 
          p: 3, 
          mb: 3, 
          borderRadius: 2 
        }}
      >
        <Button 
          startIcon={<HomeIcon />} 
          onClick={() => navigate('/dashboard')} 
          sx={{ color: 'white', mb: 2, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }}}
        >
          Back to Dashboard
        </Button>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Payroll Cost Calculator
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.9, mt: 1 }}>
          Forecast employment costs and plan for wage increases
        </Typography>
      </Paper>

      {/* Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Forecast Period</InputLabel>
              <Select
                value={weeksToForecast}
                label="Forecast Period"
                onChange={(e) => setWeeksToForecast(e.target.value)}
              >
                <MenuItem value={4}>4 Weeks (Monthly)</MenuItem>
                <MenuItem value={13}>13 Weeks (Quarterly)</MenuItem>
                <MenuItem value={26}>26 Weeks (6 Months)</MenuItem>
                <MenuItem value={52}>52 Weeks (Annual)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button 
              variant="outlined" 
              startIcon={<RefreshIcon />} 
              onClick={fetchData}
              fullWidth
            >
              Refresh Data
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button 
              variant="contained" 
              startIcon={<DownloadIcon />} 
              onClick={() => navigate('/payroll-export')}
              fullWidth
              sx={{ background: 'linear-gradient(135deg, #7c3aed, #6366f1)' }}
            >
              Payroll Export
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Future rates unavailable banner — replaces the old silent
          behaviour where the "Future" column quietly mirrored "Current"
          with no indication anything was wrong. */}
      {!payrollData.rates.increased && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Next April's rates haven't been set up yet, so future-cost forecasts aren't available.
        </Alert>
      )}
      
      {/* NMW Rate Cards */}
      <Card sx={{ mb: 3 }}>
        <CardHeader title="National Minimum Wage Rates" />
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Current Rates
                <Chip label="Now" size="small" color="success" sx={{ ml: 1 }} />
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={3}>
                  <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#dcfce7', textAlign: 'center', borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary">21+</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {payrollData.rates.current.over21 != null ? formatCurrency(payrollData.rates.current.over21) : 'N/A'}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={3}>
                  <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#dbeafe', textAlign: 'center', borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary">18-20</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {payrollData.rates.current.age18to20 != null ? formatCurrency(payrollData.rates.current.age18to20) : 'N/A'}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={3}>
                  <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#fef3c7', textAlign: 'center', borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary">Under 18</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {payrollData.rates.current.under18 != null ? formatCurrency(payrollData.rates.current.under18) : 'N/A'}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={3}>
                  <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#ede9fe', textAlign: 'center', borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary">Apprentice</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {payrollData.rates.current.apprentice != null ? formatCurrency(payrollData.rates.current.apprentice) : 'N/A'}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                April {futureAprilYear} Rates
                <Chip label="Future" size="small" color="warning" sx={{ ml: 1 }} />
              </Typography>
              {payrollData.rates.increased ? (
                <Grid container spacing={1}>
                  <Grid item xs={3}>
                    <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#059669', textAlign: 'center', borderRadius: 2 }}>
                      <Typography variant="caption" sx={{ color: 'white' }}>21+</Typography>
                      <Typography variant="body1" fontWeight="bold" sx={{ color: 'white' }}>
                        {payrollData.rates.increased.over21 != null ? formatCurrency(payrollData.rates.increased.over21) : 'N/A'}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={3}>
                    <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#2563eb', textAlign: 'center', borderRadius: 2 }}>
                      <Typography variant="caption" sx={{ color: 'white' }}>18-20</Typography>
                      <Typography variant="body1" fontWeight="bold" sx={{ color: 'white' }}>
                        {payrollData.rates.increased.age18to20 != null ? formatCurrency(payrollData.rates.increased.age18to20) : 'N/A'}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={3}>
                    <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#d97706', textAlign: 'center', borderRadius: 2 }}>
                      <Typography variant="caption" sx={{ color: 'white' }}>Under 18</Typography>
                      <Typography variant="body1" fontWeight="bold" sx={{ color: 'white' }}>
                        {payrollData.rates.increased.under18 != null ? formatCurrency(payrollData.rates.increased.under18) : 'N/A'}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={3}>
                    <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#7c3aed', textAlign: 'center', borderRadius: 2 }}>
                      <Typography variant="caption" sx={{ color: 'white' }}>Apprentice</Typography>
                      <Typography variant="body1" fontWeight="bold" sx={{ color: 'white' }}>
                        {payrollData.rates.increased.apprentice != null ? formatCurrency(payrollData.rates.increased.apprentice) : 'N/A'}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Not yet available.
                </Typography>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={8}>
          <Card>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={activeTab} onChange={handleTabChange}>
                <Tab label="Current Rates" />
                <Tab 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      April {futureAprilYear}
                      <TrendingUpIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                    </Box>
                  } 
                />
              </Tabs>
            </Box>
            <Box>
              {activeTab === 0 && (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell>Employee</TableCell>
                        <TableCell align="center">Age</TableCell>
                        <TableCell align="center">Hours/Week</TableCell>
                        <TableCell align="center">Hourly Rate</TableCell>
                        <TableCell align="center">Weekly</TableCell>
                        <TableCell align="center">{weeksToForecast} Weeks</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {payrollData.employees.map((emp) => (
                        <TableRow key={emp.id}>
                          <TableCell>
                            {emp.name || 'Unnamed'}
                            {emp.isApprentice && (
                              <Chip label="Apprentice" size="small" sx={{ ml: 1, height: 18, fontSize: '0.7rem' }} />
                            )}
                            {emp.ratesIncomplete && (
                              <Tooltip title="One or more rates used in this calculation are currently unavailable">
                                <Chip label="Incomplete" size="small" color="warning" sx={{ ml: 1, height: 18, fontSize: '0.6rem' }} />
                              </Tooltip>
                            )}
                            {emp.isSalaried && (
                              <Tooltip title={`Salaried: ${formatCurrency(emp.annualSalary)} a year. The hourly rate shown is worked out from salary and contracted hours.`}>
                                <Chip label="Salaried" size="small" color="info" sx={{ ml: 1, height: 18, fontSize: '0.6rem' }} />
                              </Tooltip>
                            )}
                            {emp.salaryNoHours && (
                              <Tooltip title="No contracted hours, so no hourly rate can be worked out. Costed at salary / 52; minimum wage can't be checked.">
                                <Chip label="No hours" size="small" color="warning" sx={{ ml: 1, height: 18, fontSize: '0.6rem' }} />
                              </Tooltip>
                            )}
                            {emp.hasSchedule === false && (
                              <Tooltip title="No working schedule set up for this employee">
                                <Chip label="No schedule" size="small" color="warning" sx={{ ml: 1, height: 18, fontSize: '0.6rem' }} />
                              </Tooltip>
                            )}
                            {emp.belowNMW && (
                              <Tooltip title={`Below the required rate for their age/status (£${emp.requiredRate?.toFixed(2)})`}>
                                <Chip label="Below NMW" size="small" color="error" sx={{ ml: 1, height: 18, fontSize: '0.6rem' }} />
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell align="center">{emp.currentAge || 'N/A'}</TableCell>
                          <TableCell align="center">
                            {emp.hours?.toFixed(1) || 0}
                          </TableCell>
                          <TableCell align="center">{formatCurrency(emp.hourlyRate || 0)}</TableCell>
                          <TableCell align="center">{formatCurrency(emp.currentPayroll || 0)}</TableCell>
                          <TableCell align="center">{formatCurrency((emp.currentPayroll || 0) * weeksToForecast)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell sx={{ fontWeight: 'bold' }} colSpan={4}>Totals</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                          {formatCurrency(currentTotals.totalPayroll)}
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                          {formatCurrency(currentTotals.totalPayroll * weeksToForecast)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              
              {activeTab === 1 && (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell>Employee</TableCell>
                        <TableCell align="center">Age (Apr)</TableCell>
                        <TableCell align="center">Hours/Week</TableCell>
                        <TableCell align="center">New Rate</TableCell>
                        <TableCell align="center">Weekly</TableCell>
                        <TableCell align="center">{weeksToForecast} Weeks</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {payrollData.employees.map((emp) => {
                        const rateChanged = emp.futureRate > (emp.hourlyRate || 0);
                        const rateDecreased = emp.futureRate < (emp.hourlyRate || 0);
                        return (
                          <TableRow key={emp.id}>
                            <TableCell>
                              {emp.name || 'Unnamed'}
                              {emp.isApprentice && (
                                <Chip label="Apprentice" size="small" sx={{ ml: 1, height: 18, fontSize: '0.7rem' }} />
                              )}
                              {emp.isSalaried && (
                                <Tooltip title={`Salaried: ${formatCurrency(emp.annualSalary)} a year. Stays on salary unless it falls below the new minimum.`}>
                                  <Chip label="Salaried" size="small" color="info" sx={{ ml: 1, height: 18, fontSize: '0.6rem' }} />
                                </Tooltip>
                              )}
                              {emp.futureRatesIncomplete && (
                                <Tooltip title="One or more future rates are currently unavailable">
                                  <Chip label="Incomplete" size="small" color="warning" sx={{ ml: 1, height: 18, fontSize: '0.6rem' }} />
                                </Tooltip>
                              )}
                            </TableCell>
                            <TableCell align="center">{emp.futureAge || 'N/A'}</TableCell>
                            <TableCell align="center">{emp.hours?.toFixed(1) || 0}</TableCell>
                            <TableCell 
                              align="center"
                              sx={{
                                fontWeight: rateChanged ? 'bold' : 'normal',
                                backgroundColor: rateChanged ? 'rgba(245, 158, 11, 0.1)' : 'inherit'
                              }}
                            >
                              {formatCurrency(emp.futureRate || 0)}
                              {rateChanged && (
                                <Chip 
                                  size="small" 
                                  label={`+${formatCurrency(emp.futureRate - (emp.hourlyRate || 0))}`} 
                                  color="warning" 
                                  sx={{ ml: 0.5, height: 18, fontSize: '0.6rem' }} 
                                />
                              )}
                              {/* Safety net: a forecast rate should never be lower
                                  than the current rate. If it ever is, say so. */}
                              {rateDecreased && (
                                <Chip
                                  size="small"
                                  label="Rate decreases"
                                  color="error"
                                  sx={{ ml: 0.5, height: 18, fontSize: '0.6rem' }}
                                />
                              )}
                            </TableCell>
                            <TableCell align="center">{formatCurrency(emp.futurePayroll || 0)}</TableCell>
                            <TableCell align="center">{formatCurrency((emp.futurePayroll || 0) * weeksToForecast)}</TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell sx={{ fontWeight: 'bold' }} colSpan={4}>Totals</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                          {formatCurrency(futureTotals.totalPayroll)}
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                          {formatCurrency(futureTotals.totalPayroll * weeksToForecast)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Card>
        </Grid>
        
        <Grid item xs={12} lg={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Summary</Typography>
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">Weekly Payroll</Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  {formatCurrency(activeTab === 0 ? currentTotals.totalPayroll : futureTotals.totalPayroll)}
                </Typography>
              </Box>
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {weeksToForecast === 52 ? 'Annual' : `${weeksToForecast}-Week`} Payroll
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  {formatCurrency((activeTab === 0 ? currentTotals.totalPayroll : futureTotals.totalPayroll) * weeksToForecast)}
                </Typography>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">Total Weekly Cost (incl. NIC and Pension; paid holiday is already in wages)</Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#059669' }}>
                  {formatCurrency(activeTab === 0 ? currentTotals.totalCost : futureTotals.totalCost)}
                </Typography>
              </Box>
              
              {calculateAnnualDifference() > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ mt: 2, p: 2, bgcolor: '#fee2e2', borderRadius: 2 }}>
                    <Typography variant="body2" color="error.dark">April Rate Increase Impact</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                      +{formatCurrency(calculateAnnualDifference())}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Additional cost over {weeksToForecast} weeks
                    </Typography>
                  </Box>
                </>
              )}
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">Employer's NIC ({weeksToForecast} weeks)</Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {formatCurrency(annualNIC)}
                </Typography>
              </Box>
              
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary">After Employment Allowance</Typography>
                {nicAfterAllowance != null ? (
                  <>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#059669' }}>
                      {formatCurrency(nicAfterAllowance)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Saving: {formatCurrency(Math.min(annualNIC, employmentAllowance))}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Employment Allowance rate unavailable
                  </Typography>
                )}
              </Box>
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">Effective NIC Rate</Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{calculateEffectiveNIC().toFixed(2)}%</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Cost Breakdown */}
      <Card>
        <CardHeader title="Employee Cost Breakdown (Weekly)" />
        <CardContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell>Employee</TableCell>
                  <TableCell align="center">Wages</TableCell>
                  <TableCell align="center">Employer NIC</TableCell>
                  <TableCell align="center">Pension</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', color: '#059669' }}>Total Cost</TableCell>
                  <TableCell align="center">Per Hour</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payrollData.employees.map((emp) => (
                  <TableRow key={emp.id}>
                    <TableCell>{emp.name || 'Unnamed'}</TableCell>
                    <TableCell align="center">
                      {formatCurrency(activeTab === 0 ? emp.currentPayroll : emp.futurePayroll)}
                    </TableCell>
                    <TableCell align="center">
                      {formatCurrency(activeTab === 0 ? emp.enicPayable : emp.futureEnicPayable)}
                    </TableCell>
                    <TableCell align="center">
                      {formatCurrency(activeTab === 0 ? emp.employerPension : emp.futureEmployerPension)}
                    </TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 'bold', color: '#059669' }}>
                      {formatCurrency(activeTab === 0 ? emp.totalWeeklyCost : emp.futureTotalWeeklyCost)}
                    </TableCell>
                    <TableCell align="center">
                      {formatCurrency(activeTab === 0 ? emp.costPerHour : emp.futureCostPerHour)}
                    </TableCell>
                  </TableRow>
                ))}

                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Totals</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(activeTab === 0 ? currentTotals.totalPayroll : futureTotals.totalPayroll)}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(activeTab === 0 ? currentTotals.totalEnic : futureTotals.totalEnic)}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(activeTab === 0 ? currentTotals.totalPension : futureTotals.totalPension)}
                  </TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', color: '#059669', fontSize: '1rem' }}>
                    {formatCurrency(activeTab === 0 ? currentTotals.totalCost : futureTotals.totalCost)}
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Info Box */}
      <Paper sx={{ p: 3, mt: 3, bgcolor: '#eff6ff', border: '1px solid #bfdbfe' }}>
        <Typography variant="subtitle2" color="primary" gutterBottom>
          Understanding Your Employment Costs
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary" paragraph>
              <strong>Employer's NIC:</strong>{' '}
              {payrollData.rates.current.enic != null
                ? `You pay ${payrollData.rates.current.enic}% on earnings above £${
                    payrollData.rates.current.enic_threshold != null
                      ? payrollData.rates.current.enic_threshold.toLocaleString()
                      : 'an unavailable threshold'
                  }${
                    payrollData.rates.current.enic_threshold != null
                      ? ` per year (£${(payrollData.rates.current.enic_threshold / 52).toFixed(2)}/week)`
                      : ''
                  }.`
                : 'NIC rate is currently unavailable.'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Employment Allowance:</strong> Most businesses can claim up to{' '}
              {payrollData.rates.current.employment_allowance != null
                ? formatCurrency(payrollData.rates.current.employment_allowance)
                : 'an unavailable amount'}{' '}
              off their annual NIC bill.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary" paragraph>
              <strong>Pension:</strong> Minimum employer contribution of{' '}
              {payrollData.rates.current.pension_rate != null
                ? `${(payrollData.rates.current.pension_rate * 100).toFixed(2)}%`
                : 'an unavailable rate'}{' '}
              of qualifying earnings.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Holiday Pay:</strong> Not added on top. Staff on regular hours are
              paid their normal wage through holiday, so the 5.6 weeks is already included in
              wages. Ad hoc extra hours do build up holiday on top, and this forecast doesn't
              include that.
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default PayrollCalculator;